import type { Passenger } from '@full-elevator/sim';
import type { Container, Graphics } from 'pixi.js';
import { COLORS, PEOPLE } from '../theme.js';
import type { Build, Look, Uniform } from './people.js';
import {
  feetUnits,
  headCentreUnits,
  hidesHair,
  isCourier,
  shouldersUnits,
  TICKET_TOP_UNITS,
  UNIFORM_COAT,
  uniformOf,
  unit,
} from './people.js';

/**
 * Draws the person holding a ticket (ADR-0018), around a ticket whose top-left
 * corner is the origin — the same space the chip's plate is drawn in. How much
 * of them there is comes from their `build`: whole, compact, or anything in
 * between (view/people.ts). Nothing at all for a bare ticket.
 *
 * Two layers: `back` is everything behind the ticket (body, head, hat), `front`
 * is the hands gripping its edges. The parts that move are drawn on their own
 * (`paintLeg`, `paintEyes`), so they can move without a redraw. One costume per
 * trait, and only for a trait the ticket already shows — the costume is how a
 * VIP is recognised, never how the game says what a VIP does.
 */
export function paintFigure(
  back: Graphics,
  front: Graphics,
  passenger: Passenger,
  look: Look,
  build: Build | null,
  width: number,
  size: number,
): void {
  back.clear();
  front.clear();
  if (!build) return;

  const k = unit(size);
  const wide = passenger.size > 1;
  const courier = isCourier(passenger);
  const uniform = uniformOf(passenger);
  const impatient = passenger.patienceMs !== null && !passenger.vip;
  const { bx, by } = grid(passenger, width, size);
  const line = { color: COLORS.ink, width: 1.5 };
  const shoulders = shouldersUnits(build);

  // What they carry on their back, peeking out past the shoulder — higher on a
  // compact person, whose shoulders are behind the ticket.
  const pack = { x: wide ? 42 : 22, top: shoulders - 3 + Math.max(0, build.stretch) * 4 };
  paintCarried(back, passenger, courier, bx(pack.x), by(pack.top), k);

  // Who the police came for, at the officer's shoulder, hood up (ADR-0029).
  if (passenger.archetype === 'arrest') paintSuspect(back, bx, by, k, shoulders, look);
  paintBody(back, wide, uniform ? UNIFORM_COAT[uniform] : look.coat, shoulders - 36, bx, by);
  paintStraps(back, uniform, wide, shoulders, bx, by, k);

  const centre = headCentreUnits(wide, build);
  const head = { x: bx(centre.x), y: by(centre.y), s: (k * centre.r) / 8 };
  // Head space: the head's centre is the origin and its radius is 8.
  const hx = (x: number): number => head.x + x * head.s;
  const hy = (y: number): number => head.y + y * head.s;

  // Matching scarves: a party reads as a party before anyone reads a stripe.
  // Under the chin when the chin shows; on a compact person the chin is
  // behind the ticket, so the scarf sits over it instead, clear of the edge.
  const scarf = passenger.groupId !== null;
  const chinShows = centre.y + centre.r < TICKET_TOP_UNITS - 2;
  if (scarf && chinShows) paintScarf(back, hx(-6), hy(7.5), head.s);

  back
    .circle(head.x, head.y, 8 * head.s)
    .fill({ color: look.skin })
    .stroke(line);
  if (!courier && !hidesHair(passenger)) paintHair(back, look, hx, hy, head.s);
  // A thin band along the ticket's edge, under the face rather than over it.
  if (scarf && !chinShows) paintScarf(back, hx(-6), -2.6 * head.s, head.s, 3);

  paintBrows(back, hx, hy, head.s, { frown: impatient, glasses: look.glasses && !courier });
  paintHeadwear(back, passenger, courier, { hx, hy, s: head.s }, build.crown);

  paintHands(front, look.skin, width, size, impatient);
  if (passenger.archetype === 'arrest') paintCuffs(front, width, size);
  if (passenger.archetype === 'stretcher') paintStretcher(front, width, size);
}

/** Head space: x and y from the head's centre, in units of an 8-radius head. */
interface HeadSpace {
  readonly hx: (x: number) => number;
  readonly hy: (y: number) => number;
  readonly s: number;
}

type Carry = (g: Graphics, x: number, y: number, k: number) => void;

/** What each uniform carries on its back, if anything (ADR-0029, ADR-0031). */
const CARRIED: Partial<Record<Uniform, Carry>> = {
  postman: paintMailBag,
  commuter: paintBackpack,
  housekeeping: paintTowels,
  cleaner: paintBucket,
  waiter: paintCloche,
  bellhop: paintSuitcases,
  cyclist: paintWheel,
  executive: paintBriefcase,
  investor: paintPortfolio,
  traveller: paintRollingCase,
  robe: paintNewspaper,
  wedding: paintBouquet,
};

/** The paramedic's bag, a courier's parcel, or what a uniform carries. */
function paintCarried(
  g: Graphics,
  passenger: Passenger,
  courier: boolean,
  x: number,
  y: number,
  k: number,
): void {
  if (passenger.archetype === 'paramedic') {
    paintMedicBag(g, x, y, k);
  } else if (courier) {
    g.roundRect(x, y, 12 * k, 18 * k, 2 * k)
      .fill({ color: PEOPLE.parcel })
      .stroke({ color: COLORS.ink, width: 1.2 });
  } else {
    const uniform = uniformOf(passenger);
    if (uniform) CARRIED[uniform]?.(g, x, y, k);
  }
}

type Wear = (g: Graphics, head: HeadSpace) => void;

/** What each uniform wears on its head (ADR-0029, ADR-0031). */
const HEADWEAR: Partial<Record<Uniform, Wear>> = {
  police: paintPoliceCap,
  postman: paintPostalCap,
  cleaner: paintHardHat,
  housekeeping: paintHeadband,
  commuter: paintHeadphones,
  bellhop: paintPillbox,
  cyclist: paintBikeHelmet,
  wedding: paintFlowers,
};

/** A courier's helmet, a uniform's cap or hat, and a VIP's top hat over all of it. */
function paintHeadwear(
  g: Graphics,
  passenger: Passenger,
  courier: boolean,
  head: HeadSpace,
  crown: number,
): void {
  if (courier) paintHelmet(g, head.hx, head.hy, head.s);
  const uniform = uniformOf(passenger);
  if (uniform) HEADWEAR[uniform]?.(g, head);
  // As tall a crown as the floor has room for (view/people.ts).
  if (passenger.vip) paintTopHat(g, head.hx, head.hy, head.s, crown);
}

/** Straps over the shoulders: an apron's, or a window cleaner's harness. */
const STRAPS: Partial<Record<Uniform, number>> = {
  housekeeping: PEOPLE.apron,
  cleaner: PEOPLE.hardHat,
};

function paintStraps(
  g: Graphics,
  uniform: Uniform | null,
  wide: boolean,
  shoulders: number,
  bx: (x: number) => number,
  by: (y: number) => number,
  k: number,
): void {
  const colour = uniform ? STRAPS[uniform] : undefined;
  if (colour === undefined) return;
  const centre = wide ? 30 : 18;
  const top = wide ? shoulders - 3 : shoulders + 1;
  for (const dx of [-4.5, 4.5]) {
    g.moveTo(bx(centre + dx), by(top))
      .lineTo(bx(centre + dx), by(TICKET_TOP_UNITS + 2))
      .stroke({ width: 2.6 * k, color: colour, cap: 'round' });
  }
}

/** The postman's bag, letters sticking out of it (ADR-0031). */
function paintMailBag(g: Graphics, x: number, y: number, k: number): void {
  const line = { color: COLORS.ink, width: 1 };
  g.rect(x + 2 * k, y - 4 * k, 5 * k, 6 * k)
    .fill({ color: PEOPLE.letters })
    .stroke(line);
  g.rect(x + 7.5 * k, y - 2.5 * k, 5 * k, 5 * k)
    .fill({ color: PEOPLE.letters })
    .stroke(line);
  g.roundRect(x, y, 14 * k, 13 * k, 2 * k)
    .fill({ color: PEOPLE.mailBag })
    .stroke({ ...line, width: 1.2 });
  g.moveTo(x, y + 4 * k)
    .lineTo(x + 14 * k, y + 4 * k)
    .stroke(line);
}

/** A commuter's backpack. */
function paintBackpack(g: Graphics, x: number, y: number, k: number): void {
  g.roundRect(x, y, 12 * k, 16 * k, 3 * k)
    .fill({ color: PEOPLE.backpack })
    .stroke({ color: COLORS.ink, width: 1.2 });
  g.roundRect(x + 2 * k, y + 8 * k, 8 * k, 6 * k, 2 * k).stroke({ color: COLORS.ink, width: 1 });
}

/** Housekeeping's stack of fresh towels. */
function paintTowels(g: Graphics, x: number, y: number, k: number): void {
  for (let i = 0; i < 3; i += 1) {
    g.roundRect(x - i * k, y + i * 4.5 * k, 14 * k, 4.5 * k, 2 * k)
      .fill({ color: PEOPLE.towels })
      .stroke({ color: COLORS.ink, width: 1 });
  }
}

/** A window cleaner's bucket, the squeegee standing in it. */
function paintBucket(g: Graphics, x: number, y: number, k: number): void {
  g.moveTo(x + 9 * k, y + 6 * k)
    .lineTo(x + 13 * k, y - 9 * k)
    .stroke({ color: COLORS.ink, width: 1.6 * k });
  g.roundRect(x + 8.5 * k, y - 11.5 * k, 10 * k, 2.8 * k, 1 * k).fill({ color: PEOPLE.visor });
  g.moveTo(x, y + 3 * k)
    .lineTo(x + 12 * k, y + 3 * k)
    .lineTo(x + 10.5 * k, y + 15 * k)
    .lineTo(x + 1.5 * k, y + 15 * k)
    .closePath()
    .fill({ color: PEOPLE.bucket })
    .stroke({ color: COLORS.ink, width: 1.2 });
}

/** Room service's silver cloche on its plate (ADR-0043). */
function paintCloche(g: Graphics, x: number, y: number, k: number): void {
  const line = { color: COLORS.ink, width: 1 };
  g.moveTo(x + 1 * k, y + 8 * k)
    .quadraticCurveTo(x + 7 * k, y - 1 * k, x + 13 * k, y + 8 * k)
    .closePath()
    .fill({ color: PEOPLE.cloche })
    .stroke({ ...line, width: 1.2 });
  g.circle(x + 7 * k, y + 1.6 * k, 1.3 * k)
    .fill({ color: PEOPLE.cloche })
    .stroke(line);
  g.roundRect(x - 1 * k, y + 8 * k, 16 * k, 2.5 * k, 1 * k)
    .fill({ color: PEOPLE.waiter })
    .stroke(line);
}

/** The bellhop's bags, one stacked on the other (ADR-0043). */
function paintSuitcases(g: Graphics, x: number, y: number, k: number): void {
  const line = { color: COLORS.ink, width: 1.2 };
  g.roundRect(x, y + 5 * k, 15 * k, 10 * k, 2 * k)
    .fill({ color: PEOPLE.suitcase })
    .stroke(line);
  g.roundRect(x + 2 * k, y - 1 * k, 11 * k, 6 * k, 1.5 * k)
    .fill({ color: PEOPLE.parcel })
    .stroke(line);
  g.moveTo(x + 5.5 * k, y - 1 * k)
    .lineTo(x + 5.5 * k, y - 3 * k)
    .lineTo(x + 9.5 * k, y - 3 * k)
    .lineTo(x + 9.5 * k, y - 1 * k)
    .stroke({ color: COLORS.ink, width: 1 });
}

/** A bicycle wheel behind the cyclist, the rest of the bike out of frame (ADR-0043). */
function paintWheel(g: Graphics, x: number, y: number, k: number): void {
  const cx = x + 7 * k;
  const cy = y + 8 * k;
  g.circle(cx, cy, 8 * k).stroke({ color: PEOPLE.visor, width: 2.2 * k });
  g.circle(cx, cy, 6 * k).stroke({ color: PEOPLE.rim, width: 1 });
  for (let i = 0; i < 4; i += 1) {
    const a = (Math.PI / 4) * i;
    g.moveTo(cx - Math.cos(a) * 6 * k, cy - Math.sin(a) * 6 * k)
      .lineTo(cx + Math.cos(a) * 6 * k, cy + Math.sin(a) * 6 * k)
      .stroke({ color: PEOPLE.rim, width: 0.8 });
  }
  g.circle(cx, cy, 1.2 * k).fill({ color: COLORS.ink });
}

/** The chief executive's briefcase (ADR-0043). */
function paintBriefcase(g: Graphics, x: number, y: number, k: number): void {
  g.moveTo(x + 4.5 * k, y + 4 * k)
    .lineTo(x + 4.5 * k, y + 1.5 * k)
    .lineTo(x + 9.5 * k, y + 1.5 * k)
    .lineTo(x + 9.5 * k, y + 4 * k)
    .stroke({ color: COLORS.ink, width: 1.2 });
  g.roundRect(x, y + 4 * k, 14 * k, 10 * k, 1.5 * k)
    .fill({ color: PEOPLE.briefcase })
    .stroke({ color: COLORS.ink, width: 1.2 });
  g.rect(x + 6 * k, y + 7 * k, 2 * k, 1.6 * k).fill({ color: PEOPLE.hatBand });
}

/** The investor's portfolio, the papers showing at its edge (ADR-0043). */
function paintPortfolio(g: Graphics, x: number, y: number, k: number): void {
  g.rect(x + 2 * k, y + 1 * k, 11 * k, 4 * k)
    .fill({ color: PEOPLE.letters })
    .stroke({ color: COLORS.ink, width: 0.9 });
  g.roundRect(x, y + 3 * k, 15 * k, 12 * k, 1.5 * k)
    .fill({ color: PEOPLE.portfolio })
    .stroke({ color: COLORS.ink, width: 1.2 });
  g.circle(x + 7.5 * k, y + 9 * k, 1.4 * k).fill({ color: PEOPLE.hatBand });
}

/** A wheeled case for the airport shuttle, its handle up (ADR-0051). */
function paintRollingCase(g: Graphics, x: number, y: number, k: number): void {
  const line = { color: COLORS.ink, width: 1.2 };
  g.moveTo(x + 4 * k, y + 2 * k)
    .lineTo(x + 4 * k, y - 6 * k)
    .lineTo(x + 10 * k, y - 6 * k)
    .lineTo(x + 10 * k, y + 2 * k)
    .stroke({ color: COLORS.ink, width: 1.2 * k });
  g.roundRect(x, y + 2 * k, 14 * k, 15 * k, 2.5 * k)
    .fill({ color: PEOPLE.rollingCase })
    .stroke(line);
  g.circle(x + 3 * k, y + 17.5 * k, 1.4 * k).fill({ color: COLORS.ink });
  g.circle(x + 11 * k, y + 17.5 * k, 1.4 * k).fill({ color: COLORS.ink });
}

/** The morning paper under a breakfast guest's arm (ADR-0051). */
function paintNewspaper(g: Graphics, x: number, y: number, k: number): void {
  g.rect(x + 1 * k, y + 2 * k, 13 * k, 10 * k)
    .fill({ color: PEOPLE.letters })
    .stroke({ color: COLORS.ink, width: 1 });
  for (const dy of [5, 7.5, 10]) {
    g.moveTo(x + 3 * k, y + dy * k)
      .lineTo(x + 12 * k, y + dy * k)
      .stroke({ color: COLORS.ink, width: 0.7 });
  }
}

/** A bride's or groom's bouquet (ADR-0051). */
function paintBouquet(g: Graphics, x: number, y: number, k: number): void {
  g.moveTo(x + 7 * k, y + 16 * k)
    .lineTo(x + 7 * k, y + 8 * k)
    .stroke({ color: PEOPLE.sage, width: 2 * k });
  for (const [dx, dy] of [
    [4, 6],
    [10, 6],
    [7, 3],
  ] as const) {
    g.circle(x + dx * k, y + dy * k, 3 * k)
      .fill({ color: PEOPLE.bouquet })
      .stroke({ color: COLORS.ink, width: 0.9 });
  }
}

/** Flowers in the couple's hair (ADR-0051). */
function paintFlowers(g: Graphics, { hx, hy, s }: HeadSpace): void {
  for (const x of [-5, 0, 5]) {
    g.circle(hx(x), hy(-8.5), 2 * s)
      .fill({ color: PEOPLE.bouquet })
      .stroke({ color: COLORS.ink, width: 0.8 });
  }
}

/** The bellhop's pillbox cap, a brass band round it (ADR-0043). */
function paintPillbox(g: Graphics, { hx, hy, s }: HeadSpace): void {
  g.roundRect(hx(-6), hy(-13), 12 * s, 7 * s, 1.5 * s)
    .fill({ color: PEOPLE.bellhopCap })
    .stroke({ color: COLORS.ink, width: 1.2 });
  g.rect(hx(-6), hy(-8.6), 12 * s, 1.8 * s).fill({ color: PEOPLE.hatBand });
}

/** A cyclist's helmet, its vents in a row (ADR-0043). */
function paintBikeHelmet(g: Graphics, { hx, hy, s }: HeadSpace): void {
  g.moveTo(hx(-9.5), hy(-1))
    .quadraticCurveTo(hx(-9), hy(-14), hx(1), hy(-13.5))
    .quadraticCurveTo(hx(10), hy(-12), hx(9.5), hy(-1))
    .closePath()
    .fill({ color: PEOPLE.bikeHelmet })
    .stroke({ color: COLORS.ink, width: 1.4 });
  for (const dx of [-4, 0, 4]) {
    g.moveTo(hx(dx), hy(-11.5))
      .lineTo(hx(dx), hy(-7))
      .stroke({ color: COLORS.ink, width: 1.1 * s });
  }
}

/** The postman's cap, an envelope for a badge. */
function paintPostalCap(g: Graphics, { hx, hy, s }: HeadSpace): void {
  g.moveTo(hx(-9), hy(-3))
    .lineTo(hx(-10), hy(-9))
    .quadraticCurveTo(hx(0), hy(-15), hx(10), hy(-9))
    .lineTo(hx(9), hy(-3))
    .closePath()
    .fill({ color: PEOPLE.postalCap })
    .stroke({ color: COLORS.ink, width: 1.2 });
  g.roundRect(hx(-9.5), hy(-4), 19 * s, 2.6 * s, 1.2 * s).fill({ color: PEOPLE.postalCap });
  g.rect(hx(-2.4), hy(-10.2), 4.8 * s, 3.2 * s)
    .fill({ color: PEOPLE.letters })
    .stroke({ color: COLORS.ink, width: 0.8 });
}

/** A window cleaner's hard hat. */
function paintHardHat(g: Graphics, { hx, hy, s }: HeadSpace): void {
  g.moveTo(hx(-9), hy(-2))
    .quadraticCurveTo(hx(-9), hy(-13), hx(0), hy(-13))
    .quadraticCurveTo(hx(9), hy(-13), hx(9), hy(-2))
    .closePath()
    .fill({ color: PEOPLE.hardHat })
    .stroke({ color: COLORS.ink, width: 1.4 });
  g.roundRect(hx(-11), hy(-3), 22 * s, 2.6 * s, 1.2 * s)
    .fill({ color: PEOPLE.hardHat })
    .stroke({ color: COLORS.ink, width: 1 });
  g.moveTo(hx(0), hy(-13)).lineTo(hx(0), hy(-4)).stroke({ color: COLORS.ink, width: 1.2 });
}

/** Housekeeping's headband. */
function paintHeadband(g: Graphics, { hx, hy, s }: HeadSpace): void {
  g.roundRect(hx(-8.2), hy(-6.5), 16.4 * s, 3.2 * s, 1.6 * s)
    .fill({ color: PEOPLE.apron })
    .stroke({ color: COLORS.ink, width: 1 });
}

/** A commuter's headphones. */
function paintHeadphones(g: Graphics, { hx, hy, s }: HeadSpace): void {
  g.moveTo(hx(-8.8), hy(0))
    .quadraticCurveTo(hx(-9.5), hy(-13), hx(0), hy(-12.5))
    .quadraticCurveTo(hx(9.5), hy(-13), hx(8.8), hy(0))
    .stroke({ color: PEOPLE.headphones, width: 2 * s });
  for (const x of [-10.5, 6.5]) {
    g.roundRect(hx(x), hy(-2.5), 4 * s, 6.5 * s, 1.6 * s).fill({ color: PEOPLE.headphones });
  }
}

/** The paramedic's bag on their back, its cross the only mark on it (ADR-0029). */
function paintMedicBag(g: Graphics, x: number, y: number, k: number): void {
  g.roundRect(x, y, 13 * k, 15 * k, 2 * k)
    .fill({ color: PEOPLE.helmet })
    .stroke({ color: COLORS.ink, width: 1.2 });
  g.rect(x + 5.5 * k, y + 3 * k, 2.4 * k, 9 * k).fill({ color: PEOPLE.medic });
  g.rect(x + 2.3 * k, y + 6.3 * k, 8.8 * k, 2.4 * k).fill({ color: PEOPLE.medic });
}

/** The police cap: a dark crown, a visor, and the badge on its front. */
function paintPoliceCap(g: Graphics, { hx, hy, s }: HeadSpace): void {
  g.moveTo(hx(-9), hy(-3))
    .lineTo(hx(-10), hy(-9))
    .quadraticCurveTo(hx(0), hy(-15), hx(10), hy(-9))
    .lineTo(hx(9), hy(-3))
    .closePath()
    .fill({ color: PEOPLE.policeCap })
    .stroke({ color: COLORS.ink, width: 1.2 });
  g.roundRect(hx(-9.5), hy(-4), 19 * s, 2.6 * s, 1.2 * s).fill({ color: PEOPLE.policeCap });
  g.circle(hx(0), hy(-8.6), 1.9 * s).fill({ color: PEOPLE.badge });
}

/** The person under arrest, behind and beside the officer, hood up. */
function paintSuspect(
  g: Graphics,
  bx: (x: number) => number,
  by: (y: number) => number,
  k: number,
  shoulders: number,
  look: Look,
): void {
  const x = bx(46);
  const y = by(shoulders - 10);
  g.circle(x, y, 7.5 * k)
    .fill({ color: PEOPLE.hood })
    .stroke({ color: COLORS.ink, width: 1.2 });
  g.circle(x - 0.8 * k, y + 1.2 * k, 4.6 * k).fill({ color: look.skin });
  g.moveTo(x - 3 * k, y + 1.4 * k)
    .lineTo(x - 0.4 * k, y + 1.4 * k)
    .stroke({ color: COLORS.ink, width: 0.9 * k, cap: 'round' });
}

/** Handcuffs at the ticket's foot: two rings and the chain between them. */
function paintCuffs(g: Graphics, width: number, size: number): void {
  const y = size + 2;
  const x = width * 0.62;
  for (const dx of [-3.6, 3.6]) {
    g.circle(x + dx, y, 2.6).stroke({ color: PEOPLE.cuffs, width: 1.6 });
  }
  g.moveTo(x - 1, y)
    .lineTo(x + 1, y)
    .stroke({ color: COLORS.ink, width: 1 });
}

/**
 * The stretcher in front of the crew: a frame on two wheels, a pillow, the
 * patient under a blanket. Wider than the ticket, low, where the legs are.
 */
function paintStretcher(g: Graphics, width: number, size: number): void {
  const y = size * 0.98;
  const x0 = -2;
  const w = width + 4;
  g.moveTo(x0 + 3, y + 7)
    .lineTo(x0 + 6, y + 13)
    .moveTo(x0 + w - 3, y + 7)
    .lineTo(x0 + w - 6, y + 13)
    .stroke({ color: COLORS.ink, width: 1.3 });
  for (const wx of [x0 + 6, x0 + w - 6]) {
    g.circle(wx, y + 14, 2.2).fill({ color: COLORS.ink });
  }
  g.roundRect(x0, y + 2, w, 5, 2)
    .fill({ color: PEOPLE.helmet })
    .stroke({ color: COLORS.ink, width: 1.2 });
  g.roundRect(x0 + 2, y - 1, 9, 4, 2).fill({ color: PEOPLE.helmet });
  g.circle(x0 + 7, y - 2, 3.4)
    .fill({ color: PEOPLE.skin[1] as number })
    .stroke({ color: COLORS.ink, width: 1 });
  g.roundRect(x0 + 11, y - 1.5, w - 14, 5, 2.5)
    .fill({ color: PEOPLE.blanket })
    .stroke({ color: COLORS.ink, width: 1 });
}

/**
 * The mockup draws an ordinary person on a ticket centred at x 18, a
 * broad-built one on a two-seat ticket centred at x 30 and 52 units wide.
 * These map its units to the chip's pixels.
 */
function grid(
  passenger: Passenger,
  width: number,
  size: number,
): { bx: (x: number) => number; by: (y: number) => number } {
  const k = unit(size);
  const wide = passenger.size > 1;
  return {
    bx: (x) => width / 2 + (x - (wide ? 30 : 18)) * (wide ? width / 52 : k),
    by: (y) => (y - TICKET_TOP_UNITS) * k,
  };
}

/** Where a head is and how big one of its units is, in the chip's pixels. */
function headOf(passenger: Passenger, build: Build, width: number, size: number) {
  const { bx, by } = grid(passenger, width, size);
  const centre = headCentreUnits(passenger.size > 1, build);
  return { x: bx(centre.x), y: by(centre.y), s: (unit(size) * centre.r) / 8 };
}

/**
 * The eyes, on their own so they can look about (view/motion.ts) without a
 * redraw. Returns the size of one head unit in pixels, which is how far they
 * may move; 0 for a bare ticket and under a courier's visor, which has none.
 */
export function paintEyes(
  g: Graphics,
  passenger: Passenger,
  build: Build | null,
  width: number,
  size: number,
): number {
  g.clear();
  if (!build || isCourier(passenger)) return 0;
  const head = headOf(passenger, build, width, size);
  for (const side of [-1, 1]) {
    g.circle(head.x + 2.7 * side * head.s, head.y + head.s, head.s).fill({ color: COLORS.ink });
  }
  return head.s;
}

/**
 * Closed eyes, drawn over the open ones for a blink (view/motion.ts). Nothing
 * for a bare ticket, and nothing under a courier's visor.
 */
export function paintLids(
  g: Graphics,
  passenger: Passenger,
  look: Look,
  build: Build | null,
  width: number,
  size: number,
): void {
  g.clear();
  if (!build || isCourier(passenger)) return;
  const { x: cx, y: cy, s } = headOf(passenger, build, width, size);
  for (const side of [-1, 1]) {
    const x = cx + 2.7 * side * s;
    const y = cy + s;
    g.circle(x, y, 1.6 * s).fill({ color: look.skin });
    g.moveTo(x - 1.3 * s, y)
      .lineTo(x + 1.3 * s, y)
      .stroke({ color: COLORS.ink, width: 1 * s, cap: 'round' });
  }
}

/** The coat behind the ticket; `drop` lowers the shoulders on a compact person. */
function paintBody(
  g: Graphics,
  wide: boolean,
  coat: number,
  drop: number,
  bx: (x: number) => number,
  by: (y: number) => number,
): void {
  if (wide) {
    // A broad build for someone who takes two seats.
    g.moveTo(bx(8), by(70))
      .quadraticCurveTo(bx(3), by(48 + drop / 2), bx(15), by(40 + drop))
      .quadraticCurveTo(bx(30), by(32 + drop), bx(45), by(40 + drop))
      .quadraticCurveTo(bx(57), by(48 + drop / 2), bx(52), by(70));
  } else {
    g.moveTo(bx(8), by(68))
      .lineTo(bx(9), by(43 + drop))
      .quadraticCurveTo(bx(9), by(36 + drop), bx(18), by(36 + drop))
      .quadraticCurveTo(bx(27), by(36 + drop), bx(27), by(43 + drop))
      .lineTo(bx(28), by(68));
  }
  g.closePath().fill({ color: coat }).stroke({ color: COLORS.ink, width: 1.5 });
}

/** Brows and glasses: what stays put while the eyes move behind them. */
function paintBrows(
  g: Graphics,
  hx: (x: number) => number,
  hy: (y: number) => number,
  s: number,
  features: { readonly frown: boolean; readonly glasses: boolean },
): void {
  for (const side of [-1, 1]) {
    if (features.frown) {
      // The brows tilt in towards the nose.
      g.moveTo(hx(4.8 * side), hy(-2.8))
        .lineTo(hx(1.6 * side), hy(-1.4))
        .stroke({ color: COLORS.ink, width: 1.3 * s, cap: 'round' });
    }
    if (features.glasses) {
      g.circle(hx(2.7 * side), hy(1), 2.4 * s).stroke({ color: COLORS.ink, width: 1 });
    }
  }
}

/**
 * Hands on the ticket's edges. Inside the row gap on both sides, so a crowd
 * takes exactly the width it took when it was only tickets.
 */
function paintHands(g: Graphics, skin: number, width: number, size: number, watch: boolean): void {
  const k = unit(size);
  const y = 15 * k;
  for (const x of [0, width]) {
    g.circle(x, y, 2.8 * (size / 30))
      .fill({ color: skin })
      .stroke({ color: COLORS.ink, width: 1 });
  }
  if (watch) {
    // The wristwatch an impatient traveller keeps looking at.
    g.roundRect(width - 2.5 * k, y - 6.5 * k, 5 * k, 3 * k, k)
      .fill({ color: PEOPLE.hatBand })
      .stroke({ color: COLORS.ink, width: 0.8 });
  }
}

/**
 * A drop of sweat once patience is below half — the face catching up with the
 * bar on the ticket, never ahead of it. Drawn every frame with the meters.
 */
export function paintSweat(
  g: Graphics,
  passenger: Passenger,
  build: Build | null,
  width: number,
  size: number,
): void {
  if (!build) return;
  const head = headOf(passenger, build, width, size);
  const s = head.s;
  const x = head.x + 10.5 * s;
  const y = head.y - 10 * s;
  g.moveTo(x, y)
    .quadraticCurveTo(x - 2 * s, y + 3 * s, x, y + 4.5 * s)
    .quadraticCurveTo(x + 2 * s, y + 3 * s, x, y)
    .closePath()
    .fill({ color: PEOPLE.sweat })
    .stroke({ color: COLORS.ink, width: 0.6 });
}

function paintScarf(g: Graphics, x: number, y: number, s: number, height = 4): void {
  g.roundRect(x, y, 12 * s, height * s, (height / 2) * s)
    .fill({ color: PEOPLE.scarf })
    .stroke({ color: COLORS.ink, width: 1 });
}

/**
 * One leg, under the coat: `which` 0 is the left. The leg turns about its hip,
 * so it can swing as they walk; its shoe is drawn separately so an impatient
 * foot can tap. Shorter on a compact person, and a little smaller shoes.
 */
export function paintLeg(
  leg: Container,
  limb: Graphics,
  shoe: Graphics,
  passenger: Passenger,
  build: Build | null,
  width: number,
  size: number,
  which: 0 | 1,
): void {
  limb.clear();
  shoe.clear();
  leg.visible = build !== null;
  if (!build) return;
  const k = unit(size);
  const wide = passenger.size > 1;
  const { bx, by } = grid(passenger, width, size);
  const x = (wide ? [20, 34] : [12, 19.5])[which] ?? 0;
  const legWidth = wide ? 6 : 4.5;
  const top = wide ? 66 : 64;
  const whole = Math.min(1, Math.max(0, build.stretch));
  const shoeHeight = 2.2 * (0.8 + 0.2 * whole);
  const sole = TICKET_TOP_UNITS + feetUnits(build);
  const ankle = sole - shoeHeight;
  limb
    .roundRect(bx(x), by(top), legWidth * k, (ankle - 1 - top) * k, 1.5 * k)
    .fill({ color: PEOPLE.legs });
  const footX = bx(x + legWidth / 2);
  shoe
    .ellipse(footX, by(ankle), (wide ? 5 : 4) * (0.85 + 0.15 * whole) * k, shoeHeight * k)
    .fill({ color: PEOPLE.shoes });
  leg.pivot.set(footX, by(top));
  leg.position.set(footX, by(top));
}

function paintHair(
  g: Graphics,
  look: Look,
  hx: (x: number) => number,
  hy: (y: number) => number,
  s: number,
): void {
  const fill = { color: look.hairColour };
  const stroke = { color: COLORS.ink, width: 1 };
  switch (look.hair) {
    case 'bald':
      return;
    case 'short':
    case 'bun':
      g.moveTo(hx(-8), hy(-1))
        .quadraticCurveTo(hx(-8), hy(-11), hx(0), hy(-11))
        .quadraticCurveTo(hx(8), hy(-11), hx(8), hy(-1))
        .quadraticCurveTo(hx(6), hy(-7), hx(0), hy(-7))
        .quadraticCurveTo(hx(-6), hy(-7), hx(-8), hy(-1))
        .closePath()
        .fill(fill)
        .stroke(stroke);
      if (look.hair === 'bun')
        g.circle(hx(0), hy(-12), 3 * s)
          .fill(fill)
          .stroke(stroke);
      return;
    case 'long':
      g.moveTo(hx(-8), hy(2))
        .quadraticCurveTo(hx(-9), hy(-11), hx(0), hy(-11))
        .quadraticCurveTo(hx(9), hy(-11), hx(8), hy(2))
        .lineTo(hx(8), hy(8))
        .quadraticCurveTo(hx(7), hy(-4), hx(0), hy(-5))
        .quadraticCurveTo(hx(-7), hy(-4), hx(-8), hy(8))
        .closePath()
        .fill(fill)
        .stroke(stroke);
      return;
    case 'curly':
      g.moveTo(hx(-9), hy(0))
        .quadraticCurveTo(hx(-11), hy(-6), hx(-7), hy(-9))
        .quadraticCurveTo(hx(-6), hy(-14), hx(0), hy(-13))
        .quadraticCurveTo(hx(6), hy(-14), hx(7), hy(-9))
        .quadraticCurveTo(hx(11), hy(-6), hx(9), hy(0))
        .quadraticCurveTo(hx(7), hy(-5), hx(0), hy(-5))
        .quadraticCurveTo(hx(-7), hy(-5), hx(-9), hy(0))
        .closePath()
        .fill(fill)
        .stroke(stroke);
      return;
  }
}

/** The courier's motorbike helmet: shell, dark visor, a stripe over the crown. */
function paintHelmet(
  g: Graphics,
  hx: (x: number) => number,
  hy: (y: number) => number,
  s: number,
): void {
  g.moveTo(hx(-9.5), hy(2))
    .quadraticCurveTo(hx(-9.5), hy(-11), hx(0), hy(-11))
    .quadraticCurveTo(hx(9.5), hy(-11), hx(9.5), hy(2))
    .lineTo(hx(9.5), hy(4))
    .lineTo(hx(-9.5), hy(4))
    .closePath()
    .fill({ color: PEOPLE.helmet })
    .stroke({ color: COLORS.ink, width: 1.5 });
  g.roundRect(hx(-7.5), hy(-4), 15 * s, 7 * s, 3 * s)
    .fill({ color: PEOPLE.visor })
    .stroke({ color: COLORS.ink, width: 1 });
  g.moveTo(hx(0), hy(-11))
    .lineTo(hx(0), hy(-5))
    .stroke({ color: COLORS.ink, width: 2 * s });
}

/** The VIP's top hat with a gold band, its crown `crown` head units tall. */
function paintTopHat(
  g: Graphics,
  hx: (x: number) => number,
  hy: (y: number) => number,
  s: number,
  crown: number,
): void {
  g.roundRect(hx(-10), hy(-10), 20 * s, 3.5 * s, 1.5 * s).fill({ color: PEOPLE.hat });
  g.roundRect(hx(-7), hy(-9 - crown), 14 * s, crown * s, 1.5 * s).fill({ color: PEOPLE.hat });
  g.rect(hx(-7), hy(-13), 14 * s, 3 * s).fill({ color: PEOPLE.hatBand });
}
